import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Lightbulb, 
  ShieldCheck, 
  Users, 
  Heart 
} from 'lucide-react';

// --- ANIMATION VARIANTS for Framer Motion ---
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// Icon animation variants
const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: { 
        scale: 1, 
        rotate: 0, 
        transition: { 
            type: "spring", 
            stiffness: 200, 
            damping: 10,
            delay: 0.2
        } 
    },
    hover: {
        scale: 1.1,
        rotate: 5,
        transition: { duration: 0.3 }
    }
};

// Card animation variants
const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: { 
            duration: 0.6,
            ease: "easeOut"
        } 
    },
    hover: {
        y: -8,
        scale: 1.02,
        transition: { 
            duration: 0.3,
            ease: "easeOut"
        }
    }
};

// --- AnimatedSection Wrapper ---
const AnimatedSection = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            id={id}
            ref={ref}
            className={`py-24 md:py-32 overflow-hidden ${className}`}
            variants={sectionVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            {children}
        </motion.section>
    );
};

const AnimatedText = ({ text, className, variants, ...props }) => {
    const words = text.split(" ");
    return (
        <motion.p className={className} variants={variants} {...props}>
            {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]" variants={itemVariants}>
                    {word}
                </motion.span>
            ))}
        </motion.p>
    );
};

// --- CORE VALUES SECTION ---
const CoreValuesSection = () => {
    const values = [
        { icon: Lightbulb, title: "Innovation", description: "We constantly push boundaries and explore new technologies to deliver cutting-edge solutions." },
        { icon: ShieldCheck, title: "Integrity", description: "Our commitment to transparency and ethical practices builds lasting trust with our partners." },
        { icon: Users, title: "Client-Centric", description: "Your success is our success. We are dedicated to understanding and achieving your goals." },
        { icon: Heart, title: "Passion", description: "We love what we do, and our passion for technology drives the quality and excellence of our work." }
    ];

    return (
        <AnimatedSection id="values" className="bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-white">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <AnimatedText text="Our Core Values." className="text-4xl md:text-6xl font-bold" variants={sectionVariants}/>
                </div>
                <motion.div 
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                    variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
                >
                    {values.map((value, index) => (
                        <motion.div 
                            key={index} 
                            className="text-center p-8 group cursor-pointer"
                            variants={cardVariants}
                            whileHover="hover"
                        >
                            <motion.div 
                                className="inline-block p-4 bg-blue-100 dark:bg-blue-900/50 rounded-full mb-4 group-hover:bg-cyan-100 dark:group-hover:bg-cyan-900/60 transition-colors duration-300"
                                variants={iconVariants}
                                whileHover="hover"
                            >
                               <value.icon className="text-cyan-500 dark:text-cyan-400 group-hover:text-cyan-600 dark:group-hover:text-cyan-300 transition-colors duration-300" size={32} />
                            </motion.div>
                            <motion.h3 
                                className="text-2xl font-bold mb-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors duration-300"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.3 + index * 0.1 }}
                            >
                                {value.title}
                            </motion.h3>
                            <motion.p 
                                className="text-gray-600 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.4 + index * 0.1 }}
                            >
                                {value.description}
                            </motion.p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </AnimatedSection>
    );
};

export default CoreValuesSection;
